import React from 'react';
import { connect } from 'react-redux';

import * as MyMath from 'helpers/MyMath';
import Canvas from 'ui/Canvas';
import withScreenSize from 'ui/hoc/withScreenSize';
import { hueSelector } from 'ducks/ui';

import 'css/ui/SwirlInteraction.css';



let mountCount = 0;

// Ball instances will be cached outside of component so they can be reused across mounts.
const ballPool = [];


class SwirlInteraction extends React.Component {

	constructor(props) {
		super(props);

		// Config
		this.ballCount = 220;
		this.hueTransitionDuration = 500;

		// Since the visual rendering of this component is done on a <canvas> element via a requestAnimationFrame
		// callback, we don't need to use standard React state to track changes. Instead we'll just update properties
		// which will always be used on the next tick. Triggering React rerenders would be much less efficient.
		// This tradeoff of foregoing reactive declarative programming for performance makes sense when we're
		// animating hundreds of objects.

		// Whether animation is running.
		this.running = false;
		// Counter for initially adding balls.
		this.initTime = 0;
		// Store minimum screen dimension from last initialization.
		this.screenSizeMin = 0;
		// Whether a hue transition is occurring.
		this.changeHue = false;
		// Balls don't change hue instantaneously, so we have to keep track of the index as we progress.
		this.hueChangeIndex = 0;
		// Actively rendered Ball instances.
		this.balls = [];

		// Interaction
		this.touching = false;
		this.pointerForce = 0;
		this.pointerX = 0;
		this.pointerY = 0;

		this.init = this.init.bind(this);
		this.applyStyle = this.applyStyle.bind(this);
		this.recycleBalls = this.recycleBalls.bind(this);
		this.handleTick = this.handleTick.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
	}

	componentDidMount() {
		mountCount++;

		this.applyStyle();
		this.init();
	}

	componentWillUnmount() {
		this.recycleBalls();
	}

	componentDidUpdate(prevProps, prevState) {
		const { hue } = this.props;

		this.init();

		if (hue !== prevProps.hue) {
			this.changeHue = true;
			this.hueChangeIndex = this.balls.length - 1;
		}
	}

	// Apply random style config for visual, keeping it tame on first mount :)
	applyStyle() {
		if (mountCount === 1) {
			this.simSpeed = 1;
			this.blendMode = 'lighter';
			this.drawTargets = false;
			this.ballAlpha = 0.2;
			this.hueRange = 35;
			this.clearAlpha = 1;
			return;
		}

		this.simSpeed = MyMath.random(0.8, 1.2);
		// The "darken" blend mode would be cool, but doesn't seem to be properly GPU accelerated currently.
		// The lag is too bad - this can be reenabled if performance is improved.
		this.blendMode = MyMath.randomChoice('lighter', 'lighter', 'source-over', 'multiply'/*, 'darken'*/);
		this.drawTargets = Math.random() < 0.2;
		this.ballAlpha = MyMath.randomChoice(0.2, 1, MyMath.random(0.2, 0.65));
		this.hueRange = MyMath.randomChoice(35, 35, MyMath.randomInt(0, 42));
		this.clearAlpha = 1;

		// Randomly make "goo"
		if (mountCount % 10 === 0) {
			this.blendMode = MyMath.randomChoice('source-over', 'source-over', 'lighter', 'multiply');
			this.simSpeed = 0.65;
			this.drawTargets = false;
			this.ballAlpha = 1;
			this.hueRange = MyMath.randomChoice(0, MyMath.randomInt(2, 5));
		}
		// Add occasional trails
		else if (!this.drawTargets && Math.random() < 0.3) {
			this.blendMode = MyMath.randomChoice('lighter', 'source-over', 'multiply'/*, 'darken'*/);
			this.clearAlpha = MyMath.random(0.1, 0.4);
		}
	}

	init() {
		// only restart animation if running for first time, or minimum screen dimension changed
		const currentScreenSizeMin = Math.min(this.props.screenWidth, this.props.screenHeight);
		if (!this.running || this.screenSizeMin !== currentScreenSizeMin) {
			this.running = true;
			this.screenSizeMin = currentScreenSizeMin;
			this.recycleBalls();
			// reset initialization frame
			this.initTime = 0;
		}
	}

	recycleBalls() {
		for (let i = this.balls.length - 1; i >= 0; i--) {
			ballPool.push(this.balls.pop());
		}
	}

	handleTick(frameTime, lag, ctx, width, height) {
		this.update(frameTime, lag, width, height);
		this.renderSwirl(ctx, width, height);
	}

	update(frameTime, lag, width, height) {
		const { balls, touching, pointerX, pointerY } = this;
		const centerX = width * 0.5;
		const centerY = height * 0.5;
		const radius = Math.min(width, height) * 0.35;
		const maxBallRadius = radius * 0.185;

		const speed = this.simSpeed * lag;

		// Ball physics config
		const drag = 0.05;
		const wallBounce = -0.9;

		// Start off slowly adding balls (one every ~2 frames).
		if (balls.length < this.ballCount) {
			this.initTime += frameTime;
			if (this.initTime > 32) {
				this.initTime = 0;
				const ball = ballPool.pop() || new Ball();
				ball.init(this.props.hue, centerX, centerY, maxBallRadius, this.hueRange);
				balls.push(ball);
			}
		}

		// Gradually change hues if needed.
		if (this.changeHue) {
			const changeCount = Math.ceil(frameTime / this.hueTransitionDuration * this.ballCount);
			const endIndex = Math.max(0, this.hueChangeIndex - changeCount);

			for (let i = this.hueChangeIndex; i >= endIndex; i--) {
				balls[i].setHue(this.props.hue);
			}

			if (endIndex > 0) {
				this.hueChangeIndex = endIndex - 1;
			} else {
				this.changeHue = false;
			}
		}


		// Define range of input force
		const inputForceRange = radius * 2.75;

		// Increase input force.
		if (touching) {
			if (this.pointerForce === 0) {
				this.pointerForce = 0.5;
			}
			this.pointerForce += 0.016 * speed;
			if (this.pointerForce > 1) {
				this.pointerForce = 1;
			}
		}

		// Step through visible balls.
		balls.forEach(ball => {
			// Movement
			ball.position += ball.speed * speed;
			const radiusOffset = radius + Math.sin((ball.position + ball.waveOffset) * ball.wavePeriod) * radius * ball.waveHeight;
			ball.targetX = Math.sin(ball.position) * radiusOffset + centerX;
			ball.targetY = -Math.cos(ball.position) * radiusOffset + centerY;

			// Apply input force
			const distanceFromPointer = MyMath.pointDist(pointerX, pointerY, ball.targetX, ball.targetY);
			if (touching && distanceFromPointer < inputForceRange) {
				const forceFalloff = Math.pow(1 - distanceFromPointer / inputForceRange, 3);
				const force = inputForceRange * forceFalloff * this.pointerForce;
				const angle = MyMath.pointAngle(pointerX, pointerY, ball.targetX, ball.targetY);
				ball.targetX += Math.sin(angle) * force;
				ball.targetY -= Math.cos(angle) * force;
			}

			// Apply force to approach target
			ball.inertiaX += ((ball.targetX - ball.x) * drag - ball.inertiaX) * drag;
			ball.inertiaY += ((ball.targetY - ball.y) * drag - ball.inertiaY) * drag;
			// Move
			ball.x += ball.inertiaX * speed;
			ball.y += ball.inertiaY * speed;
			// Check bounds
			if (ball.x < ball.radius) {
				ball.x = ball.radius;
				ball.inertiaX *= wallBounce;
			}else if (ball.x + ball.radius > width) {
				ball.x = width - ball.radius;
				ball.inertiaX *= wallBounce;
			}
			if (ball.y < ball.radius) {
				ball.y = ball.radius;
				ball.inertiaY *= wallBounce;
			}else if (ball.y + ball.radius > height) {
				ball.y = height - ball.radius;
				ball.inertiaY *= wallBounce;
			}

			// Perform initial grow/fade animation
			if (ball.scale < 1) {
				ball.scale += 0.02 * speed;
				if (ball.scale > 1) {
					ball.scale = 1;
				}
				ball.alpha = ball.scale;
			}
		});
	}

	renderSwirl(ctx, width, height) {
		if (this.clearAlpha === 1) {
			ctx.clearRect(0, 0, width, height);
		} else {
			ctx.globalCompositeOperation = 'destination-out';
			ctx.globalAlpha = this.clearAlpha;
			ctx.fillStyle = '#FFF'; // color doesn't matter
			ctx.fillRect(0, 0, width, height);
		}

		ctx.globalCompositeOperation = this.blendMode;

		this.drawTargets && ctx.beginPath();

		this.balls.forEach(ball => {
			// Fast path for non-scaled balls.
			if (ball.scale === 1) {
				ctx.globalAlpha = this.ballAlpha;
				const ballLeft = ball.x - ball.radius;
				const ballTop = ball.y - ball.radius;
				ctx.drawImage(ball.canvas, ballLeft | 0, ballTop | 0);
			}
			// Slower path for balls that require scaling.
			else {
				ctx.globalAlpha = ball.alpha * this.ballAlpha;
				ctx.save();
				ctx.translate(ball.x, ball.y);
				ctx.scale(ball.scale, ball.scale);
				ctx.drawImage(ball.canvas, -ball.radius, -ball.radius);
				ctx.restore();
			}

			if (this.drawTargets) {
				ctx.globalAlpha = 0.5;
				ctx.fillStyle = 'hsl(' + this.props.hue + ', 100%, 70%)';
				ctx.fillRect(ball.targetX - 2, ball.targetY - 2, 4, 4);
				ctx.moveTo(ball.x, ball.y);
				ctx.lineTo(ball.targetX, ball.targetY);
			}
		});


		if (this.drawTargets) {
			ctx.strokeStyle = 'hsl(' + this.props.hue + ', 100%, 70%)';
			ctx.stroke();
		}
	}

	handlePointerDown(pos) {
		this.touching = true;
	}

	handlePointerMove(pos) {
		this.pointerX = pos.x;
		this.pointerY = pos.y;
	}

	handlePointerUp(pos) {
		this.touching = false;
		this.pointerForce = 0;
	}

	render() {
		const { screenWidth, screenHeight } = this.props;

		return (
			<Canvas
				className="swirl-canvas"
				width={screenWidth}
				height={screenHeight}
				autoScale={false}
				onTick={this.handleTick}
				onPointerDown={this.handlePointerDown}
				onPointerMove={this.handlePointerMove}
				onPointerUp={this.handlePointerUp}
			/>
		);
	}
}

const mapStateToProps = state => ({
	hue: hueSelector(state)
});

export default connect(mapStateToProps)(
	withScreenSize(SwirlInteraction)
);


// Ball constructor
function Ball() {
	// sprite properties
	this.color = "";
	this.hueOffset = 0;
	this.radius = 0;
	this.x = 0;
	this.y = 0;
	this.width = 0;
	this.height = 0;
	this.scale = 1;
	this.alpha = 1;
	this.canvas = document.createElement('canvas');
	this.ctx = this.canvas.getContext('2d');
	this.redraw = false;
	// track target position separately (ball will approach this)
	this.targetX = 0;
	this.targetY = 0;
	// track inertia
	this.inertiaX = 0;
	this.inertiaY = 0;
	// position around center circle
	this.position = 0;
	// speed around circle in radians/frame
	this.speed = 0;
	// wave function params
	this.wavePeriod = 0;
	this.waveHeight = 0;
	this.waveOffset = 0;
}

Ball.prototype.init = function(hue, x, y, max_radius, hueRange=null) {
	// size
	this.radius = Math.random() * max_radius * 0.5 + max_radius * 0.5;
	this.width = this.radius * 2 + 1; // without adding a pixel, balls develop a flat edge on bottom and right side... ??
	this.height = this.width;
	this.canvas.width = this.width;
	this.canvas.height = this.height;
	// reset scale and alpha, allow ball to grow and fade in
	this.scale = 0;
	this.alpha = 0;
	// start position
	this.x = x;
	this.y = y;

	// set start color (after randomizing a bit)
	this.hueOffset = hueRange !== null
		? Math.floor(Math.random() * hueRange * 2 - hueRange)
		: this.hueOffset;

	this.setHue(hue);
	// position on circle
	var PI2 = Math.PI * 2;
	this.position = Math.random() * PI2;
	// 10 - 30 seconds for a full orbit
	this.speed = Math.random() * PI2 / 1000 + PI2 / 1800;
	// reset inertia
	this.inertiaX = 0;
	this.inertiaY = 0;
	// randomize offset wave
	this.wavePeriod = MyMath.randomInt(1, 6);
	this.waveHeight = Math.random() * 0.2;
	this.waveOffset = Math.random() * Math.PI;
};

Ball.prototype.setHue = function(hue) {
	// randomize hue a bit
	hue += this.hueOffset;
	// set color and render
	this.color = 'hsl(' + hue + ', 100%, 55%)';
	this.redraw = true;
	this.render();
};

Ball.prototype.render = function() {
	if (this.redraw) {
		this.redraw = false;
		this.ctx.clearRect(0, 0, this.width, this.height);
		this.ctx.fillStyle = this.color;
		this.ctx.beginPath();
		this.ctx.arc(this.radius, this.radius, this.radius, 0, Math.PI * 2);
		this.ctx.fill();
	}
};



// WEBPACK FOOTER //
// ./src/ui/SwirlInteraction.jsx